import React, { useState, useRef, useEffect } from 'react';

// Reusable Searchable Dropdown component
const SearchableDropdown = ({
  label,
  id,
  value,
  options, // Array of { value: string | number, label: string }
  onChange,
  onBlur,
  required,
  errorMessage,
  placeholder = "Search and select...",
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Find the label for the currently selected value to display in the input
  const selectedOption = options.find(option => String(option.value) === String(value));
  
  // Set initial display value for the input field
  useEffect(() => {
    if (selectedOption) {
        setSearchTerm(selectedOption.label);
    } else if (value === '' || value === undefined || value === null) {
        setSearchTerm(''); // Clear search term if value is cleared externally
    }
  }, [value, selectedOption]);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    // Clear the bound value immediately when typing starts
    if (value !== '') {
        onChange({ target: { id, name: id, value: '' } });
    }
  };

  const handleOptionClick = (option) => {
    setSearchTerm(option.label);
    onChange({ target: { id, name: id, value: option.value } });
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };
  
  // Handle click outside to close the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // On blur/outside click, reset input text to the selected label if a value is selected
        if (selectedOption) {
            setSearchTerm(selectedOption.label);
        } else {
             // Or clear the search term if no value is selected
             setSearchTerm('');
        }
        // Propagate the blur event to the parent form for validation/error clearing
        if (onBlur) {
            onBlur({ target: { name: id, value: value } });
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedOption, id, value, onBlur]);


  return (
    <div className="space-y-2 relative" ref={dropdownRef}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <input
        id={id}
        name={id}
        type="text"
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        autoComplete="off"
        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      
      {isOpen && (
        <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <li
                key={option.value}
                onClick={() => handleOptionClick(option)}
                className="p-2 cursor-pointer hover:bg-gray-100 text-sm"
              >
                {option.label}
              </li>
            ))
          ) : (
            <li className="p-2 text-gray-500 text-sm">No results found</li>
          )}
        </ul>
      )}
      
      {/* Hidden input to hold the actual selected value for form submission/validation */}
      <input type="hidden" id={`hidden_${id}`} name={id} value={value} />

      {errorMessage && <p className="text-red-500 text-sm mt-2">{errorMessage}</p>}
    </div>
  );
};

export default SearchableDropdown;